import {
  MultiSelect,
  NumberInput,
  SwitchInput,
  TextInput,
} from 'corso-admin-ui';
import { UploadContentType, UploadContentTypeLabel } from 'corso-types';
import { Controller } from 'react-hook-form';
import { useAutomationFormContext } from '~/features/settings/automations/hooks/useAutomationForm';
import { useCustomFields } from '~/features/settings/hooks/useCustomFields';

const uploadContentTypes = Object.values(UploadContentType);

export function EventCollectCustomFields() {
  const { control, watch, setValue } = useAutomationFormContext();

  const { data: customFieldOptions = [] } = useCustomFields();
  const isMediaUploadRequired = watch('event.params.isMediaUploadRequired');
  const showDefaultCommentField = watch('event.params.showDefaultCommentField');

  return (
    <>
      <Controller
        control={control}
        name="event.params.customFieldIds"
        render={({ field, fieldState }) => (
          <MultiSelect
            label="Fields to Collect"
            details="Select the fields to collect from the customer, fields can be added from the Custom Fields section in the Settings."
            placeholder="Select Fields to Collect"
            options={customFieldOptions.map((customField) => ({
              label: customField.displayName,
              value: `${customField.id}`,
            }))}
            value={field.value.map((value) => ({
              label: customFieldOptions.find(
                (customField) => customField.id === value,
              )?.displayName,
              value: `${value}`,
            }))}
            onChange={(selections) =>
              field.onChange(
                selections.map(({ value }) => Number.parseInt(value, 10)),
              )
            }
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="event.params.showDefaultCommentField"
        render={({ field: { onChange, value } }) => (
          <SwitchInput
            id="show-default-comment-field"
            label="Show Default Comment Field"
            details="Show the default comment field, in addition to the selected custom fields."
            checked={value}
            onChange={onChange}
          />
        )}
      />

      <Controller
        control={control}
        name="event.params.isDefaultCommentFieldRequired"
        render={({ field: { onChange, value } }) => (
          <SwitchInput
            id="show-default-comment-field"
            label="Require Default Comment Field"
            details="Require the customer to provide a default comment when submitting a claim."
            checked={value}
            hidden={!showDefaultCommentField}
            onChange={onChange}
          />
        )}
      />

      <Controller
        control={control}
        name="event.params.isMediaUploadRequired"
        render={({ field: { onChange, value } }) => (
          <SwitchInput
            id="is-media-upload-required"
            label="Require Media Uploads"
            details="Require the customer to upload media files when submitting a claim."
            checked={value}
            onChange={(e) => {
              // clear out the media upload instructions and minimum media upload amount when media upload is disabled
              if (!e.target.checked) {
                setValue('event.params.mediaUploadInstructions', undefined);
                setValue('event.params.minimumMediaUploadAmount', undefined);
              }
              onChange(e.target.checked);
            }}
          />
        )}
      />

      <Controller
        control={control}
        name="event.params.allowedContentTypes"
        render={({ field, fieldState }) => (
          <MultiSelect
            label="Accepted Media"
            details="The types of media files the customer can upload."
            placeholder="Select Accepted Media"
            options={uploadContentTypes.map((contentType) => ({
              label: UploadContentTypeLabel[contentType],
              value: contentType,
            }))}
            value={
              field.value?.map((contentType) => ({
                label: UploadContentTypeLabel[contentType],
                value: contentType,
              })) ?? []
            }
            onChange={(selections) =>
              field.onChange(
                uploadContentTypes.filter((contentType) =>
                  selections.some(({ value }) => value === contentType),
                ),
              )
            }
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="event.params.mediaUploadInstructions"
        render={({ field, fieldState }) => (
          <TextInput
            id="media-upload-instructions"
            label="Media Upload Instructions"
            details="Instructions to display to the customer when uploading media, such as images or videos. "
            {...field}
            hidden={!isMediaUploadRequired}
            value={field.value}
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="event.params.minimumMediaUploadAmount"
        render={({ field, fieldState }) => (
          <NumberInput
            id="minimum-media-upload-amount"
            label="Minimum Media Files Required"
            details="Minimum number of media files required to be uploaded by the customer."
            value={field.value}
            onChange={(e) => field.onChange(e.target.valueAsNumber)}
            hidden={!isMediaUploadRequired}
            error={fieldState.error?.message}
          />
        )}
      />
    </>
  );
}
